const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const setStoredAuth = ({ token, user }) => {
    if (token) {
        localStorage.setItem('token', token);
    }

    if (user) {
        localStorage.setItem('user', JSON.stringify(user));
    }
};

const clearStoredAuth = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
};

// Helper function for API requests
const apiRequest = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token');

    const config = {
        headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
            ...options.headers,
        },
        ...options,
    };

    try {
        const response = await fetch(`${API_URL}${endpoint}`, config);

        // Handle file download
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/zip')) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'Jarvis.zip';
            document.body.appendChild(a);
            a.click();
            a.remove();
            return { success: true };
        }

        const data = await response.json();

        if (!response.ok) {
            // A stored token the server no longer accepts means the session is
            // dead: clear it so the UI stops pretending the user is signed in.
            if (response.status === 401 && token && !endpoint.startsWith('/auth/')) {
                clearStoredAuth();
                window.location.reload();
            }

            const error = new Error(data.message || 'Something went wrong');
            error.status = response.status;
            error.data = data;
            throw error;
        }

        return data;
    } catch (error) {
        throw error;
    }
};

// Auth API
export const authAPI = {
    // Send OTP for email verification
    sendOTP: async (email, purpose = 'signup') => {
        return await apiRequest('/auth/send-otp', {
            method: 'POST',
            body: JSON.stringify({ email, purpose }),
        });
    },

    // Verify OTP
    verifyOTP: async (email, otp, purpose = 'signup') => {
        return await apiRequest('/auth/verify-otp', {
            method: 'POST',
            body: JSON.stringify({ email, otp, purpose }),
        });
    },

    // Sign up (after OTP verification)
    signup: async (userData) => {
        const data = await apiRequest('/auth/signup', {
            method: 'POST',
            body: JSON.stringify(userData),
        });

        if (data.token) {
            setStoredAuth({ token: data.token, user: data.user });
        }

        return data;
    },

    // Login
    login: async (credentials) => {
        const data = await apiRequest('/auth/login', {
            method: 'POST',
            body: JSON.stringify(credentials),
        });

        if (data.token) {
            setStoredAuth({ token: data.token, user: data.user });
        }

        return data;
    },

    exchangeOAuthCode: async (code) => {
        const data = await apiRequest('/auth/oauth/exchange', {
            method: 'POST',
            body: JSON.stringify({ code }),
        });

        if (data.token) {
            setStoredAuth({ token: data.token, user: data.user });
        }

        return data;
    },

    // Logout
    logout: () => {
        clearStoredAuth();
        window.location.href = '/';
    },

    // Forgot password (sends OTP)
    forgotPassword: async (email) => {
        return await apiRequest('/auth/forgot-password', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    },

    // Reset password with OTP
    resetPassword: async (data) => {
        return await apiRequest('/auth/reset-password', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    // Get current user
    getMe: async () => {
        return await apiRequest('/auth/me');
    },

    // Check if user is logged in
    isAuthenticated: () => {
        return !!localStorage.getItem('token');
    },

    // Get current user from storage
    getCurrentUser: () => {
        const user = localStorage.getItem('user');
        return user ? JSON.parse(user) : null;
    },

    // Get token
    getToken: () => {
        return localStorage.getItem('token');
    },

    // Persist session after social auth callback
    setSession: ({ token, user }) => {
        setStoredAuth({ token, user });
    },

    // Clear session without navigation
    clearSession: () => {
        clearStoredAuth();
    },

    // Social auth entry point
    getSocialAuthURL: (provider) => {
        return `${API_URL}/auth/${provider}`;
    },
};

// Payment API
export const paymentAPI = {
    // Create Razorpay order
    createOrder: async (orderData) => {
        return await apiRequest('/payment/create-order', {
            method: 'POST',
            body: JSON.stringify(orderData),
        });
    },

    // Verify payment after Razorpay callback
    verifyPayment: async (paymentData) => {
        return await apiRequest('/payment/verify', {
            method: 'POST',
            body: JSON.stringify(paymentData),
        });
    },

    // Check if user has purchased a product
    checkPurchase: async () => {
        return await apiRequest('/payment/check');
    },

    // Get user's purchases
    getPurchases: async () => {
        return await apiRequest('/payment/purchases');
    },

    // Request download
    requestDownload: async (product = 'Jarvis AI Pro') => {
        const response = await apiRequest('/payment/download', {
            method: 'POST',
            body: JSON.stringify({ product }),
        });

        if (response?.downloadUrl) {
            const link = document.createElement('a');
            link.href = response.downloadUrl;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            link.remove();
        }

        return response;
    },

    // Get payment status
    getPaymentStatus: async (paymentId) => {
        return await apiRequest(`/payment/status/${paymentId}`);
    },
};

// Source-code catalog API (videos merged from the SC project)
export const videosAPI = {
    // Public catalog with the caller's unlock state
    list: async ({ category, featured, q } = {}) => {
        const params = new URLSearchParams();

        if (category && category !== 'All') {
            params.set('category', category);
        }

        if (q) {
            params.set('q', q);
        }

        if (featured) {
            params.set('featured', 'true');
        }

        const query = params.toString();

        return await apiRequest(`/videos${query ? `?${query}` : ''}`);
    },

    get: async (id) => {
        return await apiRequest(`/videos/${id}`);
    },

    // Create a one-off order at this video's custom price
    createOrder: async (id) => {
        return await apiRequest(`/videos/${id}/order`, { method: 'POST' });
    },

    verifyOrder: async (id, payload) => {
        return await apiRequest(`/videos/${id}/verify`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    // Download the gated source code. A short lived one-time ticket is
    // exchanged first, then the browser fetches the archive itself so
    // multi-hundred-MB files never buffer in the page as a blob.
    download: async (id) => {
        const response = await apiRequest(`/videos/${id}/download-ticket`, {
            method: 'POST',
        });

        if (!response?.ticket) {
            throw new Error(response?.message || 'Download failed');
        }

        const link = document.createElement('a');
        link.href = `${API_URL}/videos/download/${response.ticket}`;
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();

        return { success: true };
    },
};

// Admin-only catalog and sales management
export const adminAPI = {
    listVideos: async () => {
        return await apiRequest('/admin/videos');
    },

    createVideo: async (payload) => {
        return await apiRequest('/admin/videos', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    updateVideo: async (id, payload) => {
        return await apiRequest(`/admin/videos/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
        });
    },

    deleteVideo: async (id) => {
        return await apiRequest(`/admin/videos/${id}`, {
            method: 'DELETE',
        });
    },

    getSettings: async () => {
        return await apiRequest('/admin/settings');
    },

    updateSettings: async (payload) => {
        return await apiRequest('/admin/settings', {
            method: 'PATCH',
            body: JSON.stringify(payload),
        });
    },

    getSales: async () => {
        return await apiRequest('/admin/sales');
    },
};

// Monthly all-access subscription (auto-pay)
export const subscriptionAPI = {
    getStatus: async () => {
        return await apiRequest('/subscription/status');
    },

    create: async () => {
        return await apiRequest('/subscription/create', { method: 'POST' });
    },

    verify: async (payload) => {
        return await apiRequest('/subscription/verify', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    cancel: async () => {
        return await apiRequest('/subscription/cancel', { method: 'POST' });
    },
};

export const supportAPI = {
    chat: async ({ message, history = [] }) => {
        return await apiRequest('/support/chat', {
            method: 'POST',
            body: JSON.stringify({ message, history }),
        });
    },
};

export const aboutAPI = {
    getYouTubeSnapshot: async () => {
        return await apiRequest('/about/youtube');
    },
};

export default authAPI;
